<template>
    <div class="puc-root">
        <div class="puc-topbar">
            <div class="puc-title-block">
                <span class="puc-page-title">제품당 원단위 관리</span>
            </div>

            <div class="puc-toolbar-inline">
                <span class="puc-date-label">조회 기간</span>
                <date-picker
                    v-model="dateStart"
                    type="date"
                    format="YYYY-MM-DD"
                    value-type="format"
                    placeholder="시작일"
                    :input-class="'puc-dp'"
                    @change="onDateChange"
                />
                <span class="puc-date-sep">~</span>
                <date-picker
                    v-model="dateEnd"
                    type="date"
                    format="YYYY-MM-DD"
                    value-type="format"
                    placeholder="종료일"
                    :input-class="'puc-dp'"
                    @change="onDateChange"
                />
                <div class="puc-quick-btns">
                    <button
                        v-for="q in quickOpts"
                        :key="q.value"
                        type="button"
                        class="puc-quick-btn"
                        :class="{ 'puc-quick-btn--active': activeQuick === q.value }"
                        @click="setQuickRange(q.value)"
                    >
                        {{ q.label }}
                    </button>
                </div>
            </div>
        </div>

        <div class="puc-main-body">
            <div class="puc-layout">
                <section class="puc-col puc-col--list">
                    <ProductListPanel
                        :products="products"
                        :selected-id="selectedProductId"
                        @select="onSelectProduct"
                        @add="onAddProduct"
                    />
                </section>

                <section class="puc-col puc-col--detail">
                    <ProductInfoCard
                        :product="selectedProduct"
                        :avg-intensity="avgIntensity"
                        :target-ei="null"
                        :ei-unit="eiUnit"
                        :intensity-rows="intensityRows"
                        @edit="openEditModal"
                        @delete="onDelete"
                    />
                </section>

                <section class="puc-stage">
                    <div class="puc-kpi-grid">
                        <div
                            v-for="kpi in kpiCards"
                            :key="kpi.id"
                            class="puc-kpi-card"
                            :class="[kpi.variant, { 'puc-kpi-card--featured': kpi.featured }]"
                        >
                            <div class="puc-kpi-head">
                                <span class="puc-kpi-label">{{ kpi.label }}</span>
                                <span v-if="kpi.headEnd" class="puc-kpi-head-end">{{ kpi.headEnd }}</span>
                            </div>

                            <div class="puc-kpi-main">
                                <div class="puc-kpi-value-row">
                                    <span class="puc-kpi-value">{{ kpi.value }}</span>
                                    <span v-if="kpi.badge" class="puc-kpi-badge">{{ kpi.badge }}</span>
                                </div>
                                <div v-if="kpi.helper || kpi.sub" class="puc-kpi-meta-row">
                                    <span v-if="kpi.helper" class="puc-kpi-helper">{{ kpi.helper }}</span>
                                    <span v-if="kpi.helper && kpi.sub" class="puc-kpi-meta-sep" aria-hidden="true"
                                        >·</span
                                    >
                                    <span v-if="kpi.sub" class="puc-kpi-sub">{{ kpi.sub }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <section class="puc-shell puc-shell--workspace">
                        <div class="puc-workspace-head">
                            <span class="puc-workspace-title">
                                {{ activeTab === "production" ? "생산량 입력" : "제품생산 원단위" }}
                            </span>
                            <div class="puc-center-tabs">
                                <button
                                    class="puc-center-tab"
                                    :class="{ 'puc-center-tab--active': activeTab === 'production' }"
                                    @click="activeTab = 'production'"
                                >
                                    생산량 이력
                                </button>
                                <button
                                    class="puc-center-tab"
                                    :class="{ 'puc-center-tab--active': activeTab === 'intensity' }"
                                    @click="activeTab = 'intensity'"
                                >
                                    원단위 결과
                                </button>
                            </div>
                        </div>

                        <div class="puc-tab-content">
                            <ProductionDataPanel
                                v-show="activeTab === 'production'"
                                :selected-product="selectedProduct"
                                :shared-date-start="dateStart"
                                :shared-date-end="dateEnd"
                                :api-production-rows="productProductionRows"
                                @request-production-refresh="refreshPeriodData"
                            />
                            <ProductionIntensityPanel
                                v-show="activeTab === 'intensity'"
                                :product="selectedProduct"
                                :unit-small-code="intensityTableUnitSmallCode"
                                :target-ei-unit="intensityTableTargetEiUnit"
                                :rows="intensityRows"
                                :loading="intensityLoading"
                            />
                        </div>
                    </section>
                </section>
            </div>
        </div>

        <b-modal
            v-model="showEditModal"
            title="상품 설정"
            size="lg"
            centered
            scrollable
            cancel-title="닫기"
            :ok-title="modalOkTitle"
            body-class="p-0"
            content-class="puc-edit-modal-content"
            @ok.prevent="onProductModalOk"
            @cancel="closeEditModal"
            @hidden="onEditModalHidden"
        >
            <ProductDetailPanel
                ref="productDetailPanel"
                :product="selectedProduct"
                :is-adding="isAdding"
                :all-products="products"
                @save-new="onSaveNew"
                @save-edit="onSaveEdit"
            />
        </b-modal>
    </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import backEndApi from "@src/api/backEndApi";
import ProductListPanel from "./components/ProductListPanel.vue";
import ProductDetailPanel from "./components/ProductDetailPanel.vue";
import ProductInfoCard from "./components/ProductInfoCard.vue";
import ProductionDataPanel from "./components/ProductionDataPanel.vue";
import ProductionIntensityPanel from "./components/ProductionIntensityPanel.vue";
import { buildInsertProductBody, buildUpdateProductBody, mapMasterRowToProduct } from "./config/productMapper.js";

export default {
    name: "ProductionUnitConfigMain",
    components: {
        DatePicker,
        ProductListPanel,
        ProductDetailPanel,
        ProductInfoCard,
        ProductionDataPanel,
        ProductionIntensityPanel,
    },
    data() {
        const today = new Date();
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const fmt = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return {
            products: [],
            selectedProductId: null,
            isAdding: false,
            dateStart: fmt(monthStart),
            dateEnd: fmt(today),
            activeQuick: "month",
            intensityRows: [],
            /** getProductProduction 응답의 period·previousMonth (성과 요약·전월 비교) */
            productionSummary: null,
            /** getProductProduction 응답 rows — 생산량 패널은 API 중복 호출 없이 이 값만 반영 */
            productProductionRows: [],
            /** refreshPeriodData 경합 시 이전 응답으로 상태 덮어쓰기 방지 */
            periodDataRequestId: 0,
            intensityLoading: false,
            activeTab: "production",
            showEditModal: false,
            /** 모달이 '신규 등록' 세션인지(닫을 때 폼 초기화 구분용) */
            modalAddSession: false,
        };
    },
    computed: {
        selectedProduct() {
            if (!this.selectedProductId) return null;
            return this.products.find((p) => p.product_id === this.selectedProductId) || null;
        },
        /** 상품 마스터 API `unitSmallCode` (productMapper → product_unit) */
        intensityTableUnitSmallCode() {
            const p = this.selectedProduct;
            if (!p || p.product_unit == null) return "";
            return String(p.product_unit).trim();
        },
        /** 상품 마스터 API `targetEiUnit` (productMapper → target_ei_unit) */
        intensityTableTargetEiUnit() {
            const p = this.selectedProduct;
            if (!p || p.target_ei_unit == null) return "";
            return String(p.target_ei_unit).trim();
        },
        eiUnit() {
            return this.selectedProduct && this.selectedProduct.target_ei_unit
                ? this.selectedProduct.target_ei_unit
                : "";
        },
        // 목표 원단위 KPI/카드 비활성화 시 사용
        // targetEi() {
        //     const p = this.selectedProduct;
        //     if (!p || p.target_ei == null) return null;
        //     const n = Number(p.target_ei);
        //     return Number.isFinite(n) ? n : null;
        // },
        avgIntensity() {
            const vals = this.intensityRows
                .map((r) => r.intensityValue)
                .filter((v) => v != null && Number.isFinite(Number(v)));
            if (!vals.length) return null;
            return vals.reduce((s, v) => s + Number(v), 0) / vals.length;
        },
        totalProductionQty() {
            return this.intensityRows.reduce((s, r) => s + (Number(r.productionQty) || 0), 0);
        },
        productionPeriod() {
            return this.productionSummary && this.productionSummary.period ? this.productionSummary.period : null;
        },
        productionPreviousMonth() {
            return this.productionSummary && this.productionSummary.previousMonth
                ? this.productionSummary.previousMonth
                : null;
        },
        // progressRatio() {
        //     const avg = this.avgIntensity;
        //     const target = this.targetEi;
        //     if (avg === null || target === null || target === 0) return null;
        //     return avg / target;
        // },
        modalOkTitle() {
            return this.isAdding ? "등록" : "저장";
        },
        quickOpts() {
            return [
                { value: "7d", label: "7일" },
                { value: "month", label: "이번 달" },
                { value: "prev", label: "지난 달" },
                { value: "3m", label: "3개월" },
            ];
        },
        selectedRangeLabel() {
            return `${this.dateStart || "—"} ~ ${this.dateEnd || "—"}`;
        },
        kpiCards() {
            const p = this.selectedProduct;
            const unit = p ? p.product_unit || "EA" : "EA";
            const avg = this.avgIntensity;
            const period = this.productionPeriod;
            const prevM = this.productionPreviousMonth;
            const apiQty = period && period.totalQuantity != null ? Number(period.totalQuantity) : null;
            const totalQty = apiQty != null && Number.isFinite(apiQty) && apiQty > 0 ? apiQty : this.totalProductionQty;
            const prevQty = prevM && prevM.totalQuantity != null ? Number(prevM.totalQuantity) : null;
            const prevAvgIntensity =
                prevM && prevM.avgIntensityKWhPerUnit != null && Number.isFinite(Number(prevM.avgIntensityKWhPerUnit))
                    ? Number(prevM.avgIntensityKWhPerUnit)
                    : null;
            const avgChangePct =
                avg !== null && prevAvgIntensity != null && prevAvgIntensity !== 0
                    ? (avg / prevAvgIntensity - 1) * 100
                    : null;

            const qtyTrendText =
                totalQty > 0 && prevQty != null && Number.isFinite(prevQty) && prevQty > 0
                    ? `전월 ${prevQty.toLocaleString()} 대비 ${((totalQty / prevQty - 1) * 100 >= 0 ? "+" : "") + ((totalQty / prevQty - 1) * 100).toFixed(1)}%`
                    : "조회 기간 누적 생산량";
            const avgHelper =
                avgChangePct != null
                    ? `전월 대비 ${avgChangePct >= 0 ? "+" : ""}${avgChangePct.toFixed(1)}%`
                    : "전월 비교 데이터 없음";

            const defectPct = period && period.defectRatePct != null ? Number(period.defectRatePct) : null;
            const prevDefectPct = prevM && prevM.defectRatePct != null ? Number(prevM.defectRatePct) : null;
            const defectSub =
                defectPct != null &&
                Number.isFinite(defectPct) &&
                prevDefectPct != null &&
                Number.isFinite(prevDefectPct)
                    ? `전월 ${prevDefectPct.toFixed(2)}% 대비 ${defectPct - prevDefectPct >= 0 ? "+" : ""}${(
                          defectPct - prevDefectPct
                      ).toFixed(2)}%p`
                    : "조회 기간 품질 지표";

            return [
                {
                    id: "qty",
                    label: "총 생산량",
                    headEnd: this.selectedRangeLabel,
                    value: totalQty > 0 ? totalQty.toLocaleString() : "—",
                    badge: totalQty > 0 ? unit : undefined,
                    sub: qtyTrendText,
                    featured: true,
                    variant: "kpi-card--featured",
                },
                {
                    id: "ei",
                    label: "평균 원단위",
                    value: avg !== null ? avg.toFixed(3) : "—",
                    badge: this.eiUnit || undefined,
                    helper: avgHelper,
                    sub: prevAvgIntensity != null ? `전월 평균 ${prevAvgIntensity.toFixed(3)}` : "원단위 데이터 없음",
                    variant: "kpi-card--secondary",
                },
                {
                    id: "defect",
                    label: "불량률",
                    value:
                        this.intensityLoading && defectPct == null
                            ? "조회 중…"
                            : defectPct != null && Number.isFinite(defectPct)
                              ? defectPct.toFixed(2) + "%"
                              : "—",
                    sub: defectSub,
                    variant: "kpi-card--secondary",
                },
            ];
        },
    },
    watch: {
        selectedProduct: {
            handler(p) {
                this.intensityRows = [];
                this.productionSummary = null;
                this.productProductionRows = [];
                if (!p) return;
                this.refreshPeriodData();
            },
        },
        dateStart() {
            this.activeQuick = "";
            if (this.selectedProduct) this.refreshPeriodData();
        },
        dateEnd() {
            this.activeQuick = "";
            if (this.selectedProduct) this.refreshPeriodData();
        },
    },
    mounted() {
        this.loadProducts();
    },
    methods: {
        // ── 날짜 유틸 ──
        fmtDate(d) {
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        },
        onDateChange() {
            this.activeQuick = "";
        },
        setQuickRange(type) {
            const today = new Date();
            this.activeQuick = type;
            if (type === "7d") {
                const s = new Date(today);
                s.setDate(today.getDate() - 6);
                this.dateStart = this.fmtDate(s);
                this.dateEnd = this.fmtDate(today);
            } else if (type === "month") {
                this.dateStart = this.fmtDate(new Date(today.getFullYear(), today.getMonth(), 1));
                this.dateEnd = this.fmtDate(today);
            } else if (type === "prev") {
                this.dateStart = this.fmtDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
                this.dateEnd = this.fmtDate(new Date(today.getFullYear(), today.getMonth(), 0));
            } else if (type === "3m") {
                const s = new Date(today);
                s.setMonth(today.getMonth() - 2);
                s.setDate(1);
                this.dateStart = this.fmtDate(s);
                this.dateEnd = this.fmtDate(today);
            }
        },

        // ── 원단위 조회 (KPI·차트 용) ──
        buildIntensityParams() {
            const p = this.selectedProduct;
            if (!p) return null;
            const from = parseInt(String(this.dateStart).replace(/\D/g, "").slice(0, 8), 10);
            const to = parseInt(String(this.dateEnd).replace(/\D/g, "").slice(0, 8), 10);
            if (!from || !to || Number.isNaN(from) || Number.isNaN(to)) return null;
            const lo = Math.min(from, to);
            const hi = Math.max(from, to);
            const params = { from: lo, to: hi };
            if (p.default_prod_pt_idx != null && p.default_prod_pt_idx !== "") {
                params.prodPtIdx = p.default_prod_pt_idx;
            } else if (p.pt_addr && String(p.pt_addr).trim()) {
                params.prodPtAddr = String(p.pt_addr).trim();
            } else {
                return null;
            }
            return params;
        },
        buildProductProductionParams() {
            const p = this.selectedProduct;
            if (!p) return null;
            const from = parseInt(String(this.dateStart).replace(/\D/g, "").slice(0, 8), 10);
            const to = parseInt(String(this.dateEnd).replace(/\D/g, "").slice(0, 8), 10);
            if (!from || !to || Number.isNaN(from) || Number.isNaN(to)) return null;
            const lo = Math.min(from, to);
            const hi = Math.max(from, to);
            const params = { from: lo, to: hi };
            if (p.default_prod_pt_idx != null && p.default_prod_pt_idx !== "") {
                params.ptIdxs = String(p.default_prod_pt_idx);
            } else if (p.pt_addr && String(p.pt_addr).trim()) {
                params.ptAddrs = String(p.pt_addr).trim();
            } else {
                return null;
            }
            const code = p.product_code != null ? String(p.product_code).trim() : "";
            if (code) params.productCode = code;
            return params;
        },
        async refreshPeriodData() {
            const intensityParams = this.buildIntensityParams();
            const prodParams = this.buildProductProductionParams();

            if (!intensityParams) this.intensityRows = [];
            if (!prodParams) {
                this.productionSummary = null;
                this.productProductionRows = [];
            }
            if (!intensityParams && !prodParams) return;

            const rid = (this.periodDataRequestId += 1);
            this.intensityLoading = true;
            try {
                const intPromise = intensityParams
                    ? backEndApi.peiProductionIntensity.searchProductionIntensity(intensityParams).catch(() => null)
                    : Promise.resolve(null);
                const prodPromise = prodParams
                    ? backEndApi.peiProduction.getProductProduction(prodParams).catch(() => null)
                    : Promise.resolve(null);
                const [intRes, prodRes] = await Promise.all([intPromise, prodPromise]);

                if (rid !== this.periodDataRequestId) return;

                if (intensityParams) {
                    if (intRes && intRes.status === 200 && Array.isArray(intRes.data)) {
                        this.intensityRows = intRes.data;
                    } else {
                        this.intensityRows = [];
                    }
                }

                if (prodParams) {
                    if (prodRes && prodRes.status === 200 && prodRes.data && prodRes.data.period != null) {
                        this.productionSummary = {
                            period: prodRes.data.period,
                            previousMonth: prodRes.data.previousMonth,
                        };
                        this.productProductionRows = Array.isArray(prodRes.data.rows) ? prodRes.data.rows : [];
                    } else {
                        this.productionSummary = null;
                        this.productProductionRows = [];
                    }
                }
            } finally {
                if (rid === this.periodDataRequestId) {
                    this.intensityLoading = false;
                }
            }
        },

        // ── 상품 목록 로드 ──
        isLinkedPointProduct(product) {
            if (!product) return false;
            return Boolean(
                product.pt_addr || (product.default_prod_pt_idx != null && product.default_prod_pt_idx !== ""),
            );
        },
        resolveProductPointName(product, pointNameByIdx, pointNameByAddr) {
            if (!product) return "";
            const currentName = product.pt_name != null ? String(product.pt_name).trim() : "";
            if (currentName) return currentName;
            const idxKey =
                product.default_prod_pt_idx != null && product.default_prod_pt_idx !== ""
                    ? String(product.default_prod_pt_idx)
                    : "";
            const addrKey = product.pt_addr != null ? String(product.pt_addr).trim() : "";
            return (idxKey && pointNameByIdx.get(idxKey)) || (addrKey && pointNameByAddr.get(addrKey)) || "";
        },
        async enrichProductsWithPointNames(products) {
            const list = Array.isArray(products) ? products : [];
            const needsResolve = list.some(
                (product) =>
                    this.isLinkedPointProduct(product) && !(product.pt_name != null && String(product.pt_name).trim()),
            );
            if (!needsResolve) return list;
            try {
                const res = await backEndApi.pointInfo.getPointList();
                if (!res || res.status !== 200 || !Array.isArray(res.data)) return list;
                const pointNameByIdx = new Map();
                const pointNameByAddr = new Map();
                for (let i = 0; i < res.data.length; i += 1) {
                    const point = res.data[i] || {};
                    const pointName = point.ptName != null ? String(point.ptName).trim() : "";
                    if (!pointName) continue;
                    if (point.ptIdx != null && point.ptIdx !== "") {
                        pointNameByIdx.set(String(point.ptIdx), pointName);
                    }
                    if (point.ptAddr != null && String(point.ptAddr).trim() !== "") {
                        pointNameByAddr.set(String(point.ptAddr).trim(), pointName);
                    }
                }
                return list.map((product) => {
                    const resolvedName = this.resolveProductPointName(product, pointNameByIdx, pointNameByAddr);
                    if (!resolvedName) return product;
                    if (product.pt_name != null && String(product.pt_name).trim() === resolvedName) return product;
                    return { ...product, pt_name: resolvedName };
                });
            } catch {
                return list;
            }
        },
        async loadProducts() {
            try {
                const pageSize = 200;
                let page = 1;
                const all = [];
                let total = Infinity;
                while (all.length < total) {
                    const res = await backEndApi.peiProductMaster.getProductMasterList({ page, pageSize });
                    if (res.status !== 200 || res.data == null) throw new Error("api");
                    const body = res.data;
                    total = typeof body.total === "number" ? body.total : 0;
                    const rows = body.rows || [];
                    for (let i = 0; i < rows.length; i += 1) {
                        const mapped = mapMasterRowToProduct(rows[i]);
                        if (mapped) all.push(mapped);
                    }
                    if (!rows.length) break;
                    page += 1;
                    if (page > 200) break;
                }
                this.products = await this.enrichProductsWithPointNames(all);
            } catch {
                this.alertNoti("상품 목록을 불러오지 못했습니다.");
            }
        },

        // ── 이벤트 핸들러 ──
        openEditModal() {
            this.isAdding = false;
            this.modalAddSession = false;
            this.showEditModal = true;
            // 수정 모달 진입 시 "상세" 모드(… 더보기)가 아닌 "수정" 모드로 전환
            this.$nextTick(() => {
                const panel = this.$refs.productDetailPanel;
                if (panel && this.selectedProduct) panel.startEdit?.();
            });
        },
        closeEditModal() {
            this.showEditModal = false;
        },
        async onProductModalOk(bvEvt) {
            bvEvt.preventDefault();
            const panel = this.$refs.productDetailPanel;
            const payload = panel?.getSavePayload?.();
            if (!payload) return;
            if (this.isAdding) await this.onSaveNew(payload);
            else await this.onSaveEdit(payload);
        },
        onEditModalHidden() {
            const wasAddSession = this.modalAddSession;
            this.modalAddSession = false;
            this.isAdding = false;
            this.$nextTick(() => {
                const panel = this.$refs.productDetailPanel;
                if (!panel) return;
                if (wasAddSession) panel.resetAfterAddModalClose();
                else panel.resetAfterEditModalClose();
            });
        },
        onSelectProduct(product) {
            if (!product) return;
            this.isAdding = false;
            this.modalAddSession = false;
            this.selectedProductId = product.product_id;
        },
        onAddProduct() {
            this.selectedProductId = null;
            this.isAdding = true;
            this.modalAddSession = true;
            this.showEditModal = true;
        },
        async saveProdEnergyMapWithForm(formData) {
            const addr = String(formData.pt_addr || "").trim();
            if (!addr) return;
            const energyPtAddrs = Array.isArray(formData.energyPtAddrs) ? formData.energyPtAddrs : [];
            const res = await backEndApi.peiProdEnergyMap.saveProdEnergyMap({
                prodPtAddr: addr,
                energyPtAddrs,
            });
            if (res.status !== 200) throw new Error("energyMap");
        },
        async onSaveNew(formData) {
            try {
                const res = await backEndApi.peiProductMaster.insertProductMaster(buildInsertProductBody(formData));
                if (res.status !== 200 || !res.data) throw new Error("api");
                const mapped = mapMasterRowToProduct(res.data);
                if (!mapped) throw new Error("map");
                const fallbackPtName = formData && formData.pt_name != null ? String(formData.pt_name).trim() : "";
                const p = { ...mapped, pt_name: mapped.pt_name || fallbackPtName };
                this.products = [...this.products, p];
                this.selectedProductId = p.product_id;
                this.isAdding = false;
                this.modalAddSession = false;
                this.showEditModal = false;
                let energyMapOk = true;
                try {
                    await this.saveProdEnergyMapWithForm({
                        ...formData,
                        pt_addr: p.pt_addr || formData.pt_addr,
                    });
                } catch {
                    energyMapOk = false;
                }
                this.alertNoti(
                    energyMapOk ? "등록되었습니다." : "상품은 등록되었으나 에너지 매핑 저장에 실패했습니다.",
                );
            } catch {
                this.alertNoti("등록에 실패했습니다. 상품코드 중복 여부를 확인하세요.");
            }
        },
        async onSaveEdit(formData) {
            try {
                const res = await backEndApi.peiProductMaster.updateProductMaster(buildUpdateProductBody(formData));
                if (res.status !== 200 || !res.data) throw new Error("api");
                const mapped = mapMasterRowToProduct(res.data);
                if (!mapped) throw new Error("map");
                const fallbackPtName = formData && formData.pt_name != null ? String(formData.pt_name).trim() : "";
                const p = { ...mapped, pt_name: mapped.pt_name || fallbackPtName };
                const idx = this.products.findIndex((product) => product.product_id === p.product_id);
                if (idx !== -1) this.$set(this.products, idx, p);
                this.showEditModal = false;
                let energyMapOk = true;
                try {
                    await this.saveProdEnergyMapWithForm({
                        ...formData,
                        pt_addr: p.pt_addr || formData.pt_addr,
                    });
                } catch {
                    energyMapOk = false;
                }
                this.alertNoti(
                    energyMapOk ? "저장되었습니다." : "상품은 저장되었으나 에너지 매핑 저장에 실패했습니다.",
                );
            } catch {
                this.alertNoti("저장에 실패했습니다.");
            }
        },
        async onDelete(productId) {
            try {
                const res = await backEndApi.peiProductMaster.setProductMasterEnabled({
                    productIdx: productId,
                    enabled: "N",
                });
                if (res.status !== 200) throw new Error("api");
                await this.loadProducts();
                this.selectedProductId = null;
                this.isAdding = false;
                this.showEditModal = false;
                this.alertNoti("삭제 처리되었습니다.");
            } catch {
                this.alertNoti("삭제에 실패했습니다.");
            }
        },
    },
};
</script>

<style scoped>
.puc-root {
    --puc-navy: #1b263b;
    --puc-slate-500: #64748b;
    --puc-slate-300: #cbd5e1;
    --puc-slate-200: #e2e8f0;
    --puc-shell-border: rgba(203, 213, 225, 0.84);
    --puc-shell-shadow: 0 18px 38px rgba(15, 23, 42, 0.08);

    display: flex;
    flex-direction: column;
    height: 91vh;
    min-height: 760px;
    min-width: 0;
    overflow: hidden;
    border-radius: 12px;
    background:
        radial-gradient(circle at top left, rgba(59, 130, 246, 0.14), transparent 26%),
        radial-gradient(circle at 100% 12%, rgba(148, 163, 184, 0.2), transparent 28%),
        linear-gradient(180deg, #f8fbff 0%, #f2f6fb 38%, #ecf3f8 100%);
    box-shadow:
        0 28px 68px rgba(15, 23, 42, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.puc-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 18px;
    background:
        linear-gradient(135deg, rgba(15, 23, 42, 0.98), rgba(30, 41, 59, 0.96)),
        linear-gradient(180deg, rgba(255, 255, 255, 0.05), transparent);
    border-bottom: 1px solid rgba(148, 163, 184, 0.16);
    flex-shrink: 0;
}

.puc-title-block {
    display: flex;
    align-items: center;
    min-width: 0;
}

.puc-page-title {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.03em;
    color: #f8fbff;
}

.puc-toolbar-inline {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    min-width: 0;
}

.puc-date-label {
    font-size: 11px;
    font-weight: 700;
    color: rgba(148, 163, 184, 0.92);
    white-space: nowrap;
}

.puc-date-sep {
    font-size: 11px;
    color: rgba(148, 163, 184, 0.68);
}

.puc-quick-btns {
    display: flex;
    gap: 6px;
}

.puc-quick-btn {
    height: 32px;
    padding: 0 12px;
    border-radius: 10px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    background: rgba(255, 255, 255, 0.08);
    color: rgba(226, 232, 240, 0.88);
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    transition:
        background 0.18s ease,
        border-color 0.18s ease,
        color 0.18s ease,
        transform 0.18s ease;
}

.puc-quick-btn:hover {
    background: rgba(255, 255, 255, 0.14);
    border-color: rgba(96, 165, 250, 0.28);
    color: #fff;
    transform: translateY(-1px);
}

.puc-quick-btn--active {
    border-color: rgba(96, 165, 250, 0.42);
    background: rgba(37, 99, 235, 0.28);
    color: #bfdbfe;
}

.puc-main-body {
    flex: 1;
    min-height: 0;
    padding: 14px 16px 16px;
    overflow: hidden;
}

.puc-layout {
    height: 100%;
    min-height: 0;
    display: grid;
    grid-template-columns: 300px 360px minmax(0, 1fr);
    gap: 16px;
    min-width: 0;
}

.puc-col,
.puc-shell {
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    border: 1px solid var(--puc-shell-border);
    background:
        linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(248, 250, 252, 0.92)), rgba(255, 255, 255, 0.84);
    box-shadow: var(--puc-shell-shadow);
    backdrop-filter: blur(14px);
    min-width: 0;
    min-height: 0;
    overflow: hidden;
}

.puc-col > * {
    flex: 1;
    min-height: 0;
}

.puc-stage {
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
    min-height: 0;
}

.puc-shell--workspace {
    flex: 1;
    box-shadow:
        0 18px 36px rgba(15, 23, 42, 0.08),
        inset 0 4px 0 rgba(14, 165, 233, 0.52);
}

.puc-kpi-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    min-width: 0;
}

.puc-kpi-card {
    --puc-kpi-accent: rgba(37, 99, 235, 0.64);
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 16px 18px 14px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid rgba(226, 232, 240, 0.96);
    box-shadow:
        0 16px 26px rgba(15, 23, 42, 0.06),
        inset 0 1px 0 rgba(255, 255, 255, 0.72);
    overflow: hidden;
}

.puc-kpi-card--featured {
    background:
        radial-gradient(circle at top right, rgba(255, 255, 255, 0.2), transparent 38%),
        linear-gradient(135deg, #15345a 0%, #1d4ed8 58%, #3b82f6 100%);
    box-shadow:
        0 24px 38px rgba(29, 78, 216, 0.24),
        inset 0 1px 0 rgba(255, 255, 255, 0.16);
}

.puc-kpi-card::before {
    content: "";
    position: absolute;
    inset: 0 auto auto 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, var(--puc-kpi-accent), rgba(255, 255, 255, 0));
}

.puc-kpi-card--featured::before {
    height: 100%;
    opacity: 0.14;
    background:
        linear-gradient(125deg, rgba(255, 255, 255, 0.28), rgba(255, 255, 255, 0)),
        radial-gradient(circle at top right, rgba(255, 255, 255, 0.4), transparent 44%);
}

.puc-kpi-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    min-width: 0;
}

.puc-kpi-head-end {
    font-size: 10px;
    font-weight: 600;
    line-height: 1.35;
    color: #64748b;
    text-align: right;
    flex-shrink: 0;
    max-width: 58%;
    font-variant-numeric: tabular-nums;
}

.puc-kpi-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #64748b;
    text-transform: uppercase;
}

.puc-kpi-main {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.puc-kpi-value-row {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    flex-wrap: wrap;
}

.puc-kpi-value {
    font-size: 20px;
    font-weight: 700;
    color: #1b263b;
    line-height: 1.2;
    letter-spacing: -0.03em;
    font-variant-numeric: tabular-nums;
}

.puc-kpi-badge {
    display: inline-flex;
    align-items: center;

    padding: 0 10px;

    font-size: 11px;
    font-weight: 700;
    color: #475569;
}

.puc-kpi-meta-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
}

.puc-kpi-meta-sep {
    font-size: 12px;
    color: #94a3b8;
    user-select: none;
}

.puc-kpi-helper {
    font-size: 12px;
    line-height: 1.45;
    color: #64748b;
}

.puc-kpi-card--secondary {
    --puc-kpi-accent: rgba(148, 163, 184, 0.82);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.94));
    border-color: rgba(226, 232, 240, 0.96);
}

.puc-kpi-card--featured .puc-kpi-label,
.puc-kpi-card--featured .puc-kpi-value,
.puc-kpi-card--featured .puc-kpi-helper,
.puc-kpi-card--featured .puc-kpi-sub,
.puc-kpi-card--featured .puc-kpi-meta-sep,
.puc-kpi-card--featured .puc-kpi-head-end {
    color: #f8fbff;
}

.puc-kpi-card--featured .puc-kpi-value {
    font-size: 34px;
    line-height: 1.05;
}

.puc-kpi-card--featured .puc-kpi-badge {
    border-color: rgba(255, 255, 255, 0.16);
    color: #eff6ff;
}

.puc-kpi-card--secondary .puc-kpi-label,
.puc-kpi-card--secondary .puc-kpi-value,
.puc-kpi-card--secondary .puc-kpi-helper,
.puc-kpi-card--secondary .puc-kpi-sub,
.puc-kpi-card--secondary .puc-kpi-meta-sep {
    color: #1e293b;
}

.puc-kpi-card--secondary .puc-kpi-sub {
    color: #64748b;
}

.puc-kpi-card--secondary .puc-kpi-meta-sep {
    color: #94a3b8;
}

.puc-kpi-sub {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 500;
    line-height: 1.5;
}

.puc-workspace-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.92);
    background: linear-gradient(180deg, rgba(239, 246, 255, 0.62), rgba(255, 255, 255, 0.9));
}

.puc-workspace-title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
}

.puc-center-tabs {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    flex-shrink: 0;
}

.puc-center-tab {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 14px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    background: transparent;
    border: 1px solid transparent;
    cursor: pointer;
    white-space: nowrap;
    transition:
        color 0.18s ease,
        background 0.18s ease,
        border-color 0.18s ease,
        transform 0.18s ease;
}

.puc-center-tab:hover {
    color: #334155;
    background: rgba(241, 245, 249, 0.9);
}

.puc-center-tab--active {
    color: #1d4ed8;
    background: linear-gradient(180deg, rgba(219, 234, 254, 0.92), rgba(239, 246, 255, 0.98));
    border-color: rgba(96, 165, 250, 0.3);
    box-shadow: 0 10px 18px rgba(37, 99, 235, 0.12);
    transform: translateY(-1px);
}

.puc-tab-content {
    flex: 1;
    min-height: 0;
    position: relative;
    background: linear-gradient(180deg, rgba(248, 251, 255, 0.92), rgba(255, 255, 255, 0.86));
}

.puc-edit-modal-content {
    height: min(82vh, 760px);
    overflow: hidden;
}

@media (max-width: 1520px) {
    .puc-layout {
        grid-template-columns: 280px 330px minmax(0, 1fr);
    }
}

@media (max-width: 1320px) {
    .puc-layout {
        grid-template-columns: 270px 320px minmax(0, 1fr);
    }
}

@media (max-width: 1180px) {
    .puc-layout {
        grid-template-columns: 280px minmax(0, 1fr);
        grid-template-rows: minmax(0, 1fr) minmax(420px, 1fr);
    }

    .puc-col--list {
        grid-row: 1 / span 2;
    }

    .puc-col--detail {
        grid-column: 2;
        grid-row: 1;
    }

    .puc-stage {
        grid-column: 2;
        grid-row: 2;
    }
}

@media (max-width: 1080px) {
    .puc-topbar {
        align-items: flex-start;
        padding: 12px 16px;
    }

    .puc-workspace-head {
        flex-direction: column;
        align-items: stretch;
    }

    .puc-center-tabs {
        width: 100%;
        justify-content: stretch;
    }

    .puc-center-tab {
        flex: 1 1 0;
    }
}

@media (max-width: 860px) {
    .puc-root {
        height: auto;
        min-height: 0;
        border-radius: 18px;
    }

    .puc-topbar {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }

    .puc-page-title {
        font-size: 18px;
    }

    .puc-main-body {
        padding: 14px;
    }

    .puc-layout {
        grid-template-columns: minmax(0, 1fr);
        grid-template-rows: auto auto auto;
    }

    .puc-col--list,
    .puc-col--detail,
    .puc-stage {
        grid-column: auto;
        grid-row: auto;
    }
}

@media (max-width: 640px) {
    .puc-toolbar-inline {
        justify-content: flex-start;
    }

    .puc-kpi-grid {
        grid-template-columns: minmax(0, 1fr);
    }

    .puc-center-tabs {
        flex-direction: column;
    }

    .puc-center-tab {
        width: 100%;
    }
}
</style>

<style>
.puc-root .puc-dp {
    height: 30px;
    padding: 0 10px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.92);
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 9px;
    width: 118px;
    outline: none;
    transition:
        border-color 0.18s ease,
        background 0.18s ease,
        box-shadow 0.18s ease;
}

.puc-root .puc-dp::placeholder {
    color: rgba(148, 163, 184, 0.62);
}

.puc-root .puc-dp:focus {
    border-color: rgba(96, 165, 250, 0.46);
    background: rgba(255, 255, 255, 0.14);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}
</style>
